import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudUpload,
  Assessment,
  Chat,
  Speed,
  Security,
  AutoFixHigh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  const features = [
    {
      icon: <Assessment sx={{ fontSize: 48 }} />,
      title: 'AI-Powered Analysis',
      description: 'Advanced AI reviews your presentations for quality, consistency, and impact.',
    },
    {
      icon: <AutoFixHigh sx={{ fontSize: 48 }} />,
      title: 'Actionable Insights',
      description: 'Get specific recommendations to improve every aspect of your slides.',
    },
    {
      icon: <Chat sx={{ fontSize: 48 }} />,
      title: 'Interactive Chat',
      description: 'Ask questions and get detailed explanations from our AI assistant.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'Fast Processing',
      description: 'Upload and analyze presentations in minutes, not hours.',
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'Secure & Private',
      description: 'Your presentations are securely stored and processed with enterprise-grade security.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" fontWeight={700} gutterBottom>
            Elevate Your Presentations
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            AI-powered analysis to make every slide count
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            {isAuthenticated ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CloudUpload />}
                  onClick={() => navigate('/upload')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  Upload Presentation
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/decks')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  View My Decks
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={login}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                Get Started
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" textAlign="center" fontWeight={600} gutterBottom>
          Why Choose PPT Analyzer?
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          Comprehensive AI analysis to help you create better presentations
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: 8,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'primary.light',
            }}
          >
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Ready to Get Started?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Upload your first presentation and see what AI can discover
            </Typography>
            {isAuthenticated ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<CloudUpload />}
                onClick={() => navigate('/upload')}
              >
                Upload Now
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={login}
              >
                Sign In to Continue
              </Button>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

